<script setup lang="ts">
// Shared rich composer (Tiptap) for both apps. Its v-model is the Slack WIRE
// string (plain text + `<@id|name>` / `<#id|name>` tokens), so it's a near
// drop-in for the `<textarea v-model>` it replaces: each app's send() keeps
// reading its own string and never learns about Tiptap.
//
// Capabilities: bold/italic/code/strike marks (typed AND pasted markdown, via
// Tiptap's input + paste rules) serialized to Slack mrkdwn on the wire;
// inline @-mention + #-channel chips (driven by app-supplied item sources; omit
// a source and that trigger is simply inert); a `/`-command menu (app supplies
// the registry; selecting emits `command`); staged attachments (v-model:files +
// paste-image + an exposed pickFiles(); the host still uploads on submit);
// Enter-to-send / Shift+Enter-newline, auto-grow, and a placeholder. Suggestion
// popups are driven by local reactive state (no tippy / VueRenderer) and themed
// off the shared design tokens.
import { ref, watch, onBeforeUnmount } from 'vue';
import { useEditor, EditorContent, type JSONContent } from '@tiptap/vue-3';
import { Extension, type Editor, type Extensions } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import Placeholder from '@tiptap/extension-placeholder';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import { useIcons } from '../icons';
import {
  docFromWire,
  wireFromDoc,
  CHANNEL_NODE,
  type MentionItem,
  type ChannelItem,
  type SlashCommand,
} from '../lib/composeTokens';

const props = withDefaults(defineProps<{
  /** Slack wire string: plain text + `<@id|name>` / `<#id|name>` tokens. */
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  autofocus?: boolean;
  /** Enter submits (Shift+Enter always inserts a newline). */
  submitOnEnter?: boolean;
  /** Starting height in text rows before auto-grow kicks in (was a textarea's
   *  `rows`). The field still grows with content and scrolls past its cap. */
  minRows?: number;
  /** Resolve `@` candidates for a query; omit to disable user mentions. */
  mentionItems?: (query: string) => MentionItem[] | Promise<MentionItem[]>;
  /** Resolve `#` candidates for a query; omit to disable channel refs. */
  channelItems?: (query: string) => ChannelItem[] | Promise<ChannelItem[]>;
  /** The `/`-command registry; omit to disable the slash menu. */
  slashCommands?: SlashCommand[];
}>(), {
  modelValue: '',
  placeholder: '',
  disabled: false,
  autofocus: false,
  submitOnEnter: true,
  minRows: 1,
});

const emit = defineEmits<{
  (e: 'update:modelValue', wire: string): void;
  (e: 'submit', wire: string): void;
  (e: 'command', command: SlashCommand): void;
}>();

const icons = useIcons();

// ── staged attachments ───────────────────────────────────────────────────────
// Held locally as File objects (v-model:files); the host uploads them on submit
// and clears the model. The composer renders the chips + handles paste-image and
// the file picker (pickFiles, exposed for the host's attach button); drag-drop
// routing stays host-side and pushes into this same model.
const files = defineModel<File[]>('files', { default: () => [] });
const fileInput = ref<HTMLInputElement | null>(null);

function addFiles(list: File[]): void {
  if (list.length) files.value = [...files.value, ...list];
}
function removeFile(i: number): void {
  files.value = files.value.filter((_, idx) => idx !== i);
}
function pickFiles(): void { fileInput.value?.click(); }
function onFilePick(e: Event): void {
  const el = e.target as HTMLInputElement;
  addFiles(Array.from(el.files ?? []));
  el.value = ''; // allow re-picking the same file
}

// ── suggestion popup (shared by @ / # / slash) ───────────────────────────────
type AnyItem = MentionItem | ChannelItem | SlashCommand;
const popupOpen = ref(false);
const popupKind = ref<'mention' | 'channel' | 'slash'>('mention');
const popupItems = ref<AnyItem[]>([]);
const popupActive = ref(0);
const popupStyle = ref<Record<string, string>>({});
let popupCommand: ((item: AnyItem) => void) | null = null;

function setRect(clientRect?: (() => DOMRect | null) | null): void {
  const r = clientRect?.();
  if (!r) return;
  // Composers sit at the bottom of their pane, so the menu opens UPWARD: anchor
  // its bottom just above the caret line. Reka isn't involved here (Tiptap owns
  // the trigger geometry), so we place it from the caret's clientRect.
  popupStyle.value = {
    left: `${Math.round(r.left)}px`,
    bottom: `${Math.round(window.innerHeight - r.top + 6)}px`,
  };
}

function makeRender(kind: 'mention' | 'channel' | 'slash') {
  return () => ({
    onStart: (p: { items: AnyItem[]; command: (i: AnyItem) => void; clientRect?: (() => DOMRect | null) | null }) => {
      popupKind.value = kind;
      popupItems.value = p.items;
      popupActive.value = 0;
      popupCommand = p.command;
      setRect(p.clientRect);
      popupOpen.value = true;
    },
    onUpdate: (p: { items: AnyItem[]; command: (i: AnyItem) => void; clientRect?: (() => DOMRect | null) | null }) => {
      popupItems.value = p.items;
      popupActive.value = 0;
      popupCommand = p.command;
      setRect(p.clientRect);
    },
    onKeyDown: (p: { event: KeyboardEvent }) => {
      if (!popupOpen.value) return false;
      const n = popupItems.value.length;
      if (p.event.key === 'ArrowDown') { popupActive.value = n ? (popupActive.value + 1) % n : 0; return true; }
      if (p.event.key === 'ArrowUp') { popupActive.value = n ? (popupActive.value - 1 + n) % n : 0; return true; }
      if (p.event.key === 'Enter') { selectAt(popupActive.value); return true; }
      if (p.event.key === 'Escape') { popupOpen.value = false; return true; }
      return false;
    },
    onExit: () => { popupOpen.value = false; popupItems.value = []; popupCommand = null; },
  });
}

function selectAt(i: number): void {
  const item = popupItems.value[i];
  if (item && popupCommand) popupCommand(item);
}

// ── editor ───────────────────────────────────────────────────────────────────
// Tracks the last wire we emitted, so an incoming modelValue that we just
// produced is ignored (no setContent feedback loop); a genuinely different
// value (clear after send, restore after a failed upload) re-syncs the doc.
let lastWire = props.modelValue;

function doSubmit(): boolean {
  if (!props.submitOnEnter || !editor.value) return false;
  const wire = wireFromDoc(editor.value.getJSON());
  if (!wire.trim()) return true; // swallow Enter on an empty composer
  emit('submit', wire);
  return true;
}

const extensions: Extensions = [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    bulletList: false,
    orderedList: false,
    listItem: false,
    codeBlock: false,
    horizontalRule: false,
  }),
  Placeholder.configure({ placeholder: () => props.placeholder ?? '' }),
];

if (props.mentionItems) {
  extensions.push(
    Mention.configure({
      HTMLAttributes: { class: 'hq-composer__chip hq-composer__chip--user' },
      suggestion: {
        char: '@',
        items: ({ query }: { query: string }) => Promise.resolve(props.mentionItems!(query)),
        render: makeRender('mention'),
      },
    }),
  );
}
if (props.channelItems) {
  extensions.push(
    Mention.extend({ name: CHANNEL_NODE }).configure({
      HTMLAttributes: { class: 'hq-composer__chip hq-composer__chip--channel' },
      suggestion: {
        char: '#',
        items: ({ query }: { query: string }) => Promise.resolve(props.channelItems!(query)),
        render: makeRender('channel'),
      },
    }),
  );
}
if (props.slashCommands) {
  const slashRender = makeRender('slash');
  extensions.push(
    Extension.create({
      name: 'slashCommands',
      addProseMirrorPlugins() {
        return [
          Suggestion<SlashCommand>({
            editor: this.editor,
            char: '/',
            pluginKey: new PluginKey('hqSlash'),
            allowSpaces: false,
            startOfLine: false,
            items: ({ query }) => {
              const q = query.toLowerCase();
              return (props.slashCommands ?? []).filter(
                (c) => c.label.toLowerCase().includes(q) || c.id.toLowerCase().includes(q),
              );
            },
            command: ({ editor, range, props: cmd }) => {
              editor.chain().focus().deleteRange(range).run();
              emit('command', cmd as SlashCommand);
            },
            render: slashRender,
          }),
        ];
      },
    }),
  );
}

// Enter handling rides a high-priority keymap so it beats StarterKit's
// split-block; when a suggestion popup is open it defers (returns false) so the
// popup's own Enter selects the item.
extensions.push(
  Extension.create({
    name: 'composerSubmit',
    priority: 1000,
    addKeyboardShortcuts() {
      return {
        Enter: () => (popupOpen.value ? false : doSubmit()),
        'Mod-Enter': () => (popupOpen.value ? false : doSubmit()),
      };
    },
  }),
);

const editor = useEditor({
  content: docFromWire(props.modelValue),
  editable: !props.disabled,
  autofocus: props.autofocus ? 'end' : false,
  extensions,
  editorProps: {
    attributes: { class: 'hq-composer__editor', role: 'textbox', 'aria-multiline': 'true' },
    // Paste an image/file from the clipboard -> stage it. A pure file paste is
    // consumed; a mixed text+file paste still lets the text land in the editor.
    //
    // Text is pasted as PLAIN text: we insert the clipboard's `text/plain` and
    // drop the source's HTML formatting. Otherwise a rich paste (e.g. a
    // bold-italic run copied from Gmail) becomes bold+italic marks, which the
    // composer serializes to Slack mrkdwn `*_x_*` - surfacing as literal markup
    // on a web/Studio conversation. Typed markdown still formats via the input
    // rules; only *pasted* formatting is stripped. Newlines -> hard breaks so
    // multi-line structure survives.
    handlePaste: (_view, event) => {
      const cd = event.clipboardData;
      const files = cd?.files;
      const text = cd?.getData('text/plain') ?? '';
      if (files && files.length) {
        addFiles(Array.from(files));
        if (!text) return true; // pure file paste; consumed
      }
      if (!text) return false;
      const content: JSONContent[] = [];
      text.split(/\r?\n/).forEach((line, i) => {
        if (i > 0) content.push({ type: 'hardBreak' });
        if (line) content.push({ type: 'text', text: line });
      });
      editor.value?.chain().focus().insertContent(content).run();
      return true;
    },
  },
  onUpdate: ({ editor }) => {
    const wire = wireFromDoc((editor as Editor).getJSON());
    lastWire = wire;
    emit('update:modelValue', wire);
  },
});

watch(() => props.modelValue, (val) => {
  const v = val ?? '';
  if (v === (lastWire ?? '')) return; // our own echo
  lastWire = v;
  editor.value?.commands.setContent(docFromWire(v), { emitUpdate: false });
});

watch(() => props.disabled, (d) => editor.value?.setEditable(!d));

/** Imperative focus for hosts that refocus the composer after send. */
function focus(): void { editor.value?.commands.focus('end'); }
/** Open the file picker (for the host's own attach button). */
defineExpose({ focus, pickFiles });

onBeforeUnmount(() => editor.value?.destroy());
</script>

<template>
  <div
    class="hq-composer"
    :class="{ 'hq-composer--disabled': disabled }"
    :style="{ '--hq-composer-min-rows': String(minRows) }"
  >
    <!-- Staged attachments (held until the host uploads on submit). -->
    <ul v-if="files.length" class="hq-composer__files">
      <li v-for="(f, i) in files" :key="i" class="hq-composer__file">
        <span class="hq-composer__file-name">{{ f.name }}</span>
        <button
          type="button"
          class="hq-composer__file-x"
          :aria-label="`Remove ${f.name}`"
          @click="removeFile(i)"
        >
          <component :is="icons.close" :size="12" />
        </button>
      </li>
    </ul>
    <input ref="fileInput" type="file" multiple class="hq-composer__fileinput" @change="onFilePick" />

    <EditorContent v-if="editor" :editor="editor" class="hq-composer__surface" />

    <!-- Suggestion popup, portalled so the composer's overflow never clips it.
         Positioned above the caret via popupStyle (composers sit at the bottom). -->
    <Teleport to="body">
      <div
        v-if="popupOpen && popupItems.length"
        class="hq-composer__menu"
        :style="popupStyle"
        role="listbox"
      >
        <button
          v-for="(item, i) in popupItems"
          :key="(item as { id: string }).id"
          type="button"
          class="hq-composer__opt"
          :class="{ 'hq-composer__opt--active': i === popupActive }"
          role="option"
          :aria-selected="i === popupActive"
          @mousedown.prevent="selectAt(i)"
          @mouseenter="popupActive = i"
        >
          <template v-if="popupKind === 'mention'">
            <img
              v-if="(item as MentionItem).avatarUrl"
              class="hq-composer__avatar"
              :src="(item as MentionItem).avatarUrl"
              alt=""
            />
            <span v-else class="hq-composer__avatar hq-composer__avatar--initial">
              {{ (item.label || '?').charAt(0).toUpperCase() }}
            </span>
            <span class="hq-composer__opt-label">{{ item.label }}</span>
          </template>
          <template v-else-if="popupKind === 'channel'">
            <span class="hq-composer__hash">#</span>
            <span class="hq-composer__opt-label">{{ item.label }}</span>
          </template>
          <template v-else>
            <span class="hq-composer__opt-label">{{ item.label }}</span>
            <span v-if="(item as SlashCommand).hint" class="hq-composer__opt-hint">
              {{ (item as SlashCommand).hint }}
            </span>
          </template>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.hq-composer { width: 100%; }
.hq-composer--disabled { opacity: 0.6; pointer-events: none; }

/* Auto-grow: the contenteditable grows with content up to a cap, then scrolls. */
.hq-composer__surface :deep(.hq-composer__editor) {
  /* line-height is 1.5, so each row is 1.5em; minRows sets the start height. */
  min-height: calc(var(--hq-composer-min-rows, 1) * 1.5em);
  max-height: 40vh;
  overflow-y: auto;
  outline: none;
  color: var(--ink-primary, currentColor);
  font: inherit;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.hq-composer__surface :deep(.hq-composer__editor p) { margin: 0; }
/* iOS Safari auto-zooms into a focused contenteditable whose font-size is
   < 16px (the editor is `font: inherit`, usually smaller), jumping the
   viewport. Pin to 16px on touch devices - the only place the zoom fires. */
@media (pointer: coarse) {
  .hq-composer__surface :deep(.hq-composer__editor) { font-size: 16px; }
}

/* Staged-attachment chips. */
.hq-composer__fileinput { display: none; }
.hq-composer__files {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1, 4px);
  margin: 0 0 var(--space-1, 4px);
  padding: 0;
  list-style: none;
}
.hq-composer__file {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 200px;
  padding: 2px 4px 2px 8px;
  border: 1px solid var(--border-subtle, #ddd);
  border-radius: var(--radius-chip, 6px);
  background: var(--surface-sunken, #f3f3f3);
  font-size: var(--text-xs, 11px);
  color: var(--ink-primary, currentColor);
}
.hq-composer__file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hq-composer__file-x {
  display: inline-grid;
  place-items: center;
  border: 0;
  background: transparent;
  color: var(--ink-subtle, #9aa);
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  line-height: 0;
}
.hq-composer__file-x:hover { background: var(--surface-panel, #fff); color: var(--ink-primary, currentColor); }

/* Placeholder (Tiptap Placeholder extension marks the empty first line). */
.hq-composer__surface :deep(.hq-composer__editor p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  color: var(--ink-subtle, #9aa);
}

/* Inserted mention / channel chips. */
.hq-composer__surface :deep(.hq-composer__chip) {
  border-radius: var(--radius-chip, 5px);
  padding: 0 4px;
  font-weight: 500;
  white-space: nowrap;
}
.hq-composer__surface :deep(.hq-composer__chip--user) {
  background: var(--accent-soft, color-mix(in oklab, var(--accent-default, #36c) 16%, transparent));
  color: var(--accent-default, #36c);
}
.hq-composer__surface :deep(.hq-composer__chip--channel) {
  background: var(--surface-sunken, #eef);
  color: var(--ink-primary, currentColor);
}

/* (Suggestion-popup styles live in the unscoped block below - it's teleported
   to <body>, so a scoped hash wouldn't reach it.) */
</style>

<style>
.hq-composer__menu {
  position: fixed;
  z-index: 9000;
  min-width: 180px;
  max-width: min(320px, calc(100vw - 16px));
  max-height: 248px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: var(--space-1, 4px);
  background: var(--surface-panel, #fff);
  border: 1px solid var(--border-subtle, #ddd);
  border-radius: var(--radius-card, 10px);
  box-shadow: var(--shadow-overlay, 0 8px 28px -6px rgba(0, 0, 0, 0.28));
}
.hq-composer__opt {
  display: flex;
  align-items: center;
  gap: var(--space-2, 8px);
  width: 100%;
  padding: var(--space-2, 6px) var(--space-2, 8px);
  border: 0;
  border-radius: var(--radius-input, 7px);
  background: transparent;
  color: var(--ink-primary, currentColor);
  font: inherit;
  font-size: var(--text-sm, 13px);
  text-align: left;
  cursor: pointer;
}
.hq-composer__opt--active { background: var(--surface-sunken, #eef); }
.hq-composer__avatar {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}
.hq-composer__avatar--initial {
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--accent-text, #fff);
  background: var(--accent-default, #36c);
}
.hq-composer__hash {
  flex: 0 0 auto;
  width: 22px;
  text-align: center;
  font-weight: 700;
  color: var(--ink-subtle, #9aa);
}
.hq-composer__opt-label { flex: 1 1 auto; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hq-composer__opt-hint { flex: 0 0 auto; font-size: var(--text-xs, 11px); color: var(--ink-subtle, #9aa); }
</style>
